import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Menu, X } from 'lucide-react';
import { trackEvent } from '../../lib/analytics';
import Logo from '../Logo';
import ThemeToggle from '../ThemeToggle';
import WwwMobileNavPanel from '../WwwMobileNavPanel';
import { WWW_TAP_TARGET_CLASS } from '../../lib/wwwTapTarget';
import {
  WWW_MOBILE_NAV_BAR_ATTR,
  WWW_MOBILE_NAV_LINKS_CLASS,
} from '../../lib/wwwMobileNav';

/** Fixed nav is viewport-relative; html safe-area padding in index.css does not move it. */
export const LANDING_NAV_SAFE_AREA_CLASS = 'pt-[env(safe-area-inset-top,0px)]';

export const MARKETING_NAV_ATTR = 'data-landing-nav';
export const MARKETING_NAV_TESTID = 'marketing-nav';
export const MARKETING_MOBILE_NAV_ID = 'marketing-mobile-nav';

/** Opaque page-card fill so light-mode section scroll does not ghost through blur. */
export const MARKETING_NAV_CLASS =
  'fixed top-0 left-0 right-0 z-50 bg-[var(--surface-card)] border-b border-[var(--border-subtle)]';

/**
 * Clears the fixed marketing nav on public doc pages (About, Intervals, Install, Contact, legal).
 * Phone: `html` already insets `safe-area-inset-top` (`src/index.css`), and the bar is `h-14` (3.5rem).
 * The extra `1rem` is the gap under that bar. A second safe-area plus `PUBLIC_DOC_MAIN` `py-12`
 * left a tall empty band under the header.
 * `sm+` still adds `safe-area-inset-top` with the taller bar (`h-16` / `h-20`). That inset is
 * usually 0 on tablet and desktop, so it does not recreate the phone double-count.
 */
export const PUBLIC_MARKETING_PAGE_PAD_CLASS =
  'pt-[calc(3.5rem+1rem+var(--consent-banner-offset,0px))] sm:pt-[calc(6rem+env(safe-area-inset-top,0px)+var(--consent-banner-offset,0px))]';

function handleNavGetStarted() {
  trackEvent('landing_cta_click', {
    cta: 'nav-get-started',
    destination: '/register',
    timestamp: new Date().toISOString(),
  });
}

export function MarketingNav({ mobileMenuOpen, onMobileMenuOpenChange }) {
  const closeMenu = useCallback(() => {
    onMobileMenuOpenChange(false);
  }, [onMobileMenuOpenChange]);

  return (
    <nav
      data-landing-nav
      data-testid={MARKETING_NAV_TESTID}
      className={MARKETING_NAV_CLASS}
    >
      <div
        {...{ [WWW_MOBILE_NAV_BAR_ATTR]: true }}
        className={LANDING_NAV_SAFE_AREA_CLASS}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16 lg:h-20">
            <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
              <span className="dark:hidden">
                <Logo size={58} showText textClass="text-2xl sm:text-3xl" />
              </span>
              <span className="hidden dark:inline-flex">
                <Logo size={58} variant="dark" showText textClass="text-2xl sm:text-3xl" />
              </span>
            </Link>

            <div className="hidden sm:flex items-center gap-4">
              <Link
                to="/pricing"
                className="text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white font-medium px-4 py-2 transition-colors"
              >
                Pricing
              </Link>
              <Link
                to="/login"
                data-www-control="login"
                className={`${WWW_TAP_TARGET_CLASS} inline-flex items-center text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white font-medium px-4 py-2 transition-colors`}
              >
                Sign In
              </Link>
              <ThemeToggle className="text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-white" />
              <Link
                to="/register"
                onClick={handleNavGetStarted}
                className="btn-primary inline-flex items-center gap-2 px-5 py-2.5"
              >
                Get Started
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="sm:hidden flex items-center gap-1">
              <ThemeToggle className="text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-white" />
              <button
                type="button"
                aria-expanded={mobileMenuOpen}
                aria-controls={MARKETING_MOBILE_NAV_ID}
                onClick={() => onMobileMenuOpenChange(!mobileMenuOpen)}
                className={`${WWW_TAP_TARGET_CLASS} inline-flex items-center justify-center p-2 text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-white`}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <WwwMobileNavPanel open={mobileMenuOpen} id={MARKETING_MOBILE_NAV_ID}>
        <div className={WWW_MOBILE_NAV_LINKS_CLASS}>
          <Link
            to="/pricing"
            onClick={closeMenu}
            className="block text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white font-medium py-2 transition-colors"
          >
            Pricing
          </Link>
          <Link
            to="/install"
            onClick={closeMenu}
            className="block text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white font-medium py-2 transition-colors"
          >
            Install
          </Link>
          <Link
            to="/login"
            onClick={closeMenu}
            data-www-control="login"
            className={`${WWW_TAP_TARGET_CLASS} flex items-center text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white font-medium py-2 transition-colors`}
          >
            Sign In
          </Link>
          <Link
            to="/register"
            onClick={() => {
              closeMenu();
              handleNavGetStarted();
            }}
            className="btn-primary block w-full text-center px-5 py-3"
          >
            Get Started
          </Link>
        </div>
      </WwwMobileNavPanel>
    </nav>
  );
}
